// utils/elementFormat/email.js
//
// How an `email` element NORMALISES and RENDERS (docs/plans/data-types.md,
// decision 7 and the frozen wire contract): trimmed, with the DOMAIN
// lower-cased, and refused when it is not shaped `local@domain.tld`.
//
// PURE AND DEPENDENCY-FREE ON PURPOSE, and vendored byte-identical into
// keepr-web at `src/utils/elementFormat/email.js` so the form's inline
// feedback and the API's 400 `invalid_email` can never disagree about which
// addresses exist. MIRRORED in keepr-web — change them together. Nothing here
// may require() anything, and nothing here may know about Mongo, Boom or the
// request.
//
// Why the domain and not the whole address is lower-cased: the domain half is
// case-insensitive by RFC 1035, so folding it is lossless and makes
// `Sam@Example.COM` and `sam@example.com` one value for search, KQL exact
// match and de-duplication. The LOCAL half is case-SENSITIVE by RFC 5321 —
// rare in practice, but the platform does not get to decide that someone
// else's mailbox is the same mailbox, so it is stored as typed.
//
// Why the syntax check is deliberately narrower than RFC 5322: the RFC admits
// quoted locals, comments, bare IP-literal domains and single-label hosts,
// none of which a person types into a contact form and all of which read as
// typos when they appear there. The rule below is the one the user was
// promised in the plan — "no whitespace, exactly one @, a dot in the domain,
// ≤ 254 characters" — because a validator that accepts `sam@localhost` while
// the user meant `sam@localhost.com` has failed at the only job it has. It
// does NOT attempt to decide whether the mailbox EXISTS; nothing short of
// sending mail can.

'use strict';

// The SMTP forward-path limit (RFC 5321 §4.5.3.1): 256 octets including the
// surrounding angle brackets, so 254 for the address itself.
const MAX_LENGTH = 254;

// One or more characters that are neither whitespace nor '@'. The '@' count
// is checked separately so "two @s" can be told apart from "no @ at all".
const LOCAL_RE = /^[^\s@]+$/;

// Dot-separated labels of letters, digits and hyphens, a hyphen never on the
// edge of a label, and at least two labels — the "a dot in the domain" rule.
// Unicode letters are admitted so an internationalised domain typed in its
// native script is not rejected out of hand; punycode ('xn--p1ai') passes as
// ordinary ASCII.
//
// The final label (the TLD) must be two or more characters and must START
// with a letter. That single extra condition is what rejects the bare IP
// literal `sam@192.168.0.1`, which is legal by RFC but is never what a person
// filling in a contact card meant to type.
const D = 'A-Za-z0-9\\u00a1-\\uffff';
const DOMAIN_LABEL = `[${D}](?:[${D}-]*[${D}])?`;
const TLD = `[A-Za-z\\u00a1-\\uffff](?:[${D}-]*[${D}])`;
const DOMAIN_RE = new RegExp(`^(?:${DOMAIN_LABEL}\\.)+${TLD}$`);

const toStr = v => (v === null || v === undefined ? '' : String(v));

/**
 * normalizeEmail(raw) -> { value, valid }
 *
 *   value  the address as it will be STORED — trimmed, domain lower-cased —
 *          and '' whenever `valid` is false, so a caller that stores the
 *          result without checking cannot persist a malformed address.
 *   valid  the address is shaped `local@domain.tld`.
 *
 * A blank input is not valid HERE: an empty email element stores '', but
 * emptiness is the caller's question, not this function's, and treating an
 * untouched form field as a validation failure would be wrong.
 */
function normalizeEmail(raw) {
	const text = toStr(raw).trim();
	if (!text) return { value: '', valid: false };
	if (text.length > MAX_LENGTH) return { value: '', valid: false };

	const at = text.indexOf('@');
	// Exactly one '@': `a@b@c.com` is a typo, not an address.
	if (at <= 0 || text.indexOf('@', at + 1) !== -1) return { value: '', valid: false };

	const local = text.slice(0, at);
	const domain = text.slice(at + 1);
	if (!LOCAL_RE.test(local)) return { value: '', valid: false };
	if (!DOMAIN_RE.test(domain)) return { value: '', valid: false };

	return { value: `${local}@${domain.toLowerCase()}`, valid: true };
}

/** True when `raw` is an address this platform will store. */
function isValidEmail(raw) {
	return normalizeEmail(raw).valid;
}

/**
 * mailtoUrl(value) -> 'mailto:sam@example.com' | ''
 *
 * The href behind the Compose action (decision 8, "values carry their
 * actions"). A stored address contains no whitespace, no '?' and no '&' — the
 * characters that would change what a mailto: URL MEANS — so it goes through
 * verbatim rather than percent-encoded, which is what every mail client
 * expects to see. A value that is not a valid address has no action: the
 * caller renders no trigger rather than one that opens a broken draft.
 */
function mailtoUrl(value) {
	const r = normalizeEmail(value);
	return r.valid ? `mailto:${r.value}` : '';
}

/**
 * formatEmail(value) -> string
 *
 * An address reads exactly as it is stored — there is no local convention to
 * apply and nothing to abbreviate. It exists so every element type has one
 * formatter with one signature, and so a legacy value from before the type
 * existed is shown as typed rather than dropped.
 */
function formatEmail(value) {
	return toStr(value).trim();
}

module.exports = {
	MAX_LENGTH,
	normalizeEmail,
	isValidEmail,
	formatEmail,
	mailtoUrl,
};
